import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Watch } from "@/assets/icons/ds";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import { SubscriptionInfoBox } from "@/components/Layout/SubscriptionInfoBox/SubscriptionInfoBox";
import { SimCardDetail } from "@/services/model";
import { hasWatchSim } from "@/utils/deviceUtils";
import { getCurrency } from "@/utils/translationHelpers";

/**
 * "Your Watch SIM": the persistent product card - what's booked and what it
 * costs, or the booking prompt when nothing is. Always visible, unlike
 * `WatchSimStatus`, which only exists once a Watch SIM is booked.
 */
export const WatchSimInfo = ({
  simDetail,
  action,
}: {
  simDetail: SimCardDetail;
  action?: ReactNode;
}) => {
  const { t } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.watchSim.display",
  });
  const { t: tDefault } = useTranslation();

  const isBooked = hasWatchSim(simDetail);
  // getCurrency formats the whole "CHF 4.00" string, joined with a non-breaking
  // space (Intl.NumberFormat's de-CH currency style) rather than a plain " " -
  // split on any whitespace so the code and amount can carry Figma's two
  // different type styles.
  const [currencyCode, ...amountParts] = isBooked
    ? getCurrency(simDetail.priceWatch ?? 0, "CHF").split(/\s+/)
    : [];

  return (
    <CardModule
      dataTestid="watch-sim-info"
      title={t("title")}
      actions={action}
      bodyGap={2}
    >
      <SubscriptionInfoBox className="flex items-center gap-3">
        <Watch className="size-6 shrink-0 text-foreground-primary" />
        <p className="truncate-safe text-body-lg font-semibold leading-md text-foreground-primary">
          {isBooked
            ? tDefault("Common.products.watchSim")
            : t("noActiveOption")}
        </p>
      </SubscriptionInfoBox>

      {isBooked && simDetail.priceWatch !== undefined && (
        <SubscriptionInfoBox className="flex items-center gap-2">
          <span className="text-body-xs text-foreground-secondary">
            {currencyCode}
          </span>
          <span className="text-body-lg font-semibold leading-md text-foreground-primary">
            {amountParts.join(" ")}
          </span>
        </SubscriptionInfoBox>
      )}
    </CardModule>
  );
};
